// src/dashboard/CompanySettings.jsx

import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { AuthContext } from '../context/AuthContext';
import { db } from '../config/firebaseConfig';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';

/**
 * CompanySettings (enhanced)
 * - Sem inline styles (usa classes .la do foundation)
 * - i18n com kebab-case
 * - Firestore: users/{uid}/company_settings/profile
 * - Máscaras simples (CNPJ/CPF/CEP/telefone/WhatsApp) por funções
 * - Autodetecção de timezone via Intl.* (aplicada se vazio)
 * - Autosave opcional (debounce 800ms) + botão Salvar
 */
export default function CompanySettings() {
  const intl = useIntl();
  const { user } = useContext(AuthContext);

  // -------------------------------
  // State
  // -------------------------------
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);

  const [form, setForm] = useState({
    // Company info
    companyName: '',
    tradeName: '',
    website: '',
    // Legal identifiers
    cnpj: '',
    cpf: '',
    stateRegistration: '',
    // Address
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zip: '',
    // Contacts
    companyEmail: '',
    companyPhone: '',
    whatsapp: '',
    // Preferences
    language: 'pt',
    timezone: '', // será sugerido pelo browser se vazio
  });

  const lastSavedRef = useRef(JSON.stringify(form));
  const debounceRef = useRef(null);

  // -------------------------------
  // Validators & Derived
  // -------------------------------
  const isValid = useMemo(() => {
    const websiteOk = form.website && /^https?:\/\//i.test(form.website.trim());
    const nameOk = !!form.companyName?.trim();
    return websiteOk && nameOk;
  }, [form.website, form.companyName]);

  // -------------------------------
  // Mask helpers
  // -------------------------------
  const onlyDigits = (v = '') => v.replace(/\D+/g, '');

  function maskCNPJ(v = '') {
    const d = onlyDigits(v).slice(0, 14);
    return d
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }

  function maskCPF(v = '') {
    const d = onlyDigits(v).slice(0, 11);
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  function maskCEP(v = '') {
    const d = onlyDigits(v).slice(0, 8);
    return d.replace(/(\d{5})(\d{0,3})/, (m, a, b) => (b ? `${a}-${b}` : a));
  }

  function maskPhoneBR(v = '') {
    // 10 ou 11 dígitos -> (XX) XXXX-XXXX | (XX) XXXXX-XXXX
    const d = onlyDigits(v).slice(0, 11);
    if (d.length <= 10) {
      return d
        .replace(/(\d{0,2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return d
      .replace(/(\d{0,2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }

  // -------------------------------
  // Load & suggest timezone
  // -------------------------------
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid) return;
      try {
        setLoading(true);
        const ref = doc(db, 'users', user.uid, 'company_settings', 'profile');
        const snap = await getDoc(ref);
        let data = {};
        if (snap.exists()) data = snap.data();

        const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
        const timezone = data.timezone || form.timezone || browserTZ;

        if (mounted) {
          const next = { ...form, ...data, timezone };
          setForm(next);
          lastSavedRef.current = JSON.stringify(next);
        }
      } catch (err) {
        console.error('CompanySettings: load error', err);
        toast.error(intl.formatMessage({ id: 'toast.load-error' }));
        if (mounted) setStatus('error');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // -------------------------------
  // Change handler + masks
  // -------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextVal = value;
    if (name === 'cnpj') nextVal = maskCNPJ(value);
    if (name === 'cpf') nextVal = maskCPF(value);
    if (name === 'zip') nextVal = maskCEP(value);
    if (name === 'companyPhone' || name === 'whatsapp') nextVal = maskPhoneBR(value);
    setForm((prev) => ({ ...prev, [name]: nextVal }));
    setStatus('idle');
  };

  // -------------------------------
  // Save (manual & autosave)
  // -------------------------------
  const doSave = async ({ silent = false } = {}) => {
    if (!user?.uid) return;
    if (!isValid) {
      if (!silent) toast.warn(intl.formatMessage({ id: 'validation.fix-errors' }));
      return;
    }
    try {
      setSaving(true);
      setStatus('saving');
      const ref = doc(db, 'users', user.uid, 'company_settings', 'profile');
      await setDoc(
        ref,
        { ...form, updatedAt: serverTimestamp() },
        { merge: true }
      );
      lastSavedRef.current = JSON.stringify(form);
      setStatus('saved');
      if (!silent) toast.success(intl.formatMessage({ id: 'toast.saved-success' }));
    } catch (err) {
      console.error('CompanySettings: save error', err);
      setStatus('error');
      if (!silent) toast.error(intl.formatMessage({ id: 'toast.saved-error' }));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSave({ silent: false });
  };

  // -------------------------------
  // Debounced autosave
  // -------------------------------
  useEffect(() => {
    if (!autosaveEnabled) return;
    if (!user?.uid) return;

    const current = JSON.stringify(form);
    if (current === lastSavedRef.current) return;
    if (!isValid) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSave({ silent: true });
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [form, isValid, autosaveEnabled, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <div className="la dashboard-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          {intl.formatMessage({ id: 'company-settings.title', defaultMessage: 'Company Settings' })}
        </h1>
        <p className="page-subtitle">
          {intl.formatMessage({ id: 'company-settings.subtitle', defaultMessage: 'Manage your company profile, legal info, and defaults.' })}
        </p>
      </div>

      <form className="form-vertical" onSubmit={handleSubmit} noValidate>
        {/* Toolbar */}
        <div className="toolbar">
          <div className="toolbar-left form-status">
            {status === 'saving' && (
              <span className="muted">{intl.formatMessage({ id: 'common.saving', defaultMessage: 'Saving…' })}</span>
            )}
            {status === 'saved' && (
              <span className="success">{intl.formatMessage({ id: 'company-settings.status-saved', defaultMessage: 'All changes saved.' })}</span>
            )}
            {status === 'error' && (
              <span className="danger">{intl.formatMessage({ id: 'company-settings.status-error', defaultMessage: 'There was an error.' })}</span>
            )}
          </div>
          <div className="toolbar-right">
            <label className="switch">
              <input
                type="checkbox"
                checked={autosaveEnabled}
                onChange={(e) => setAutosaveEnabled(e.target.checked)}
              />
              <span className="switch-label">
                {autosaveEnabled
                  ? intl.formatMessage({ id: 'company-settings.autosave-on', defaultMessage: 'Autosave: ON' })
                  : intl.formatMessage({ id: 'company-settings.autosave-off', defaultMessage: 'Autosave: OFF' })}
              </span>
            </label>

            <button className="btn primary" type="submit" disabled={saving || !isValid}>
              {saving
                ? intl.formatMessage({ id: 'common.saving', defaultMessage: 'Saving…' })
                : intl.formatMessage({ id: 'common.save', defaultMessage: 'Save settings' })}
            </button>
          </div>
        </div>

        {/* Company Info */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">
              {intl.formatMessage({ id: 'company-settings.company-info', defaultMessage: 'Company Information' })}
            </h2>
            <p className="card-subtitle">
              {intl.formatMessage({ id: 'company-settings.company-info.help', defaultMessage: 'This information is used across the platform and in communications.' })}
            </p>
          </div>
          <div className="card-content form-grid two-col">
            <div className="form-field">
              <label className="label" htmlFor="companyName">
                {intl.formatMessage({ id: 'company-settings.name', defaultMessage: 'Legal name' })}
              </label>
              <input
                id="companyName"
                name="companyName"
                className="input"
                type="text"
                value={form.companyName}
                onChange={handleChange}
                required
              />
              {!form.companyName?.trim() && (
                <div className="field-hint danger">{intl.formatMessage({ id: 'validation.required' })}</div>
              )}
            </div>
            <div className="form-field">
              <label className="label" htmlFor="tradeName">
                {intl.formatMessage({ id: 'company-settings.trade-name', defaultMessage: 'Trade name' })}
              </label>
              <input
                id="tradeName"
                name="tradeName"
                className="input"
                type="text"
                value={form.tradeName}
                onChange={handleChange}
              />
            </div>
            <div className="form-field col-span-2">
              <label className="label" htmlFor="website">
                {intl.formatMessage({ id: 'company-settings.website', defaultMessage: 'Website (required, with http/https)' })}
              </label>
              <input
                id="website"
                name="website"
                className="input"
                type="url"
                placeholder={intl.formatMessage({ id: 'company-settings.website.placeholder', defaultMessage: 'https://example.com' })}
                value={form.website}
                onChange={handleChange}
                required
              />
              {(!form.website || !/^https?:\/\//i.test(form.website.trim())) && (
                <div className="field-hint danger">{intl.formatMessage({ id: 'validation.website-required' })}</div>
              )}
            </div>
          </div>
        </section>

        {/* Legal */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">{intl.formatMessage({ id: 'company-settings.legal', defaultMessage: 'Legal' })}</h2>
            <p className="card-subtitle">
              {intl.formatMessage({ id: 'company-settings.legal.help', defaultMessage: 'Brazil-specific fields are optional for companies outside Brazil.' })}
            </p>
          </div>
          <div className="card-content form-grid three-col">
            <div className="form-field">
              <label className="label" htmlFor="cnpj">{intl.formatMessage({ id: 'company-settings.cnpj', defaultMessage: 'CNPJ' })}</label>
              <input id="cnpj" name="cnpj" className="input" type="text" value={form.cnpj} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="cpf">{intl.formatMessage({ id: 'company-settings.cpf', defaultMessage: 'CPF (owner)' })}</label>
              <input id="cpf" name="cpf" className="input" type="text" value={form.cpf} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="stateRegistration">
                {intl.formatMessage({ id: 'company-settings.state-registration', defaultMessage: 'State Registration' })}
              </label>
              <input id="stateRegistration" name="stateRegistration" className="input" type="text" value={form.stateRegistration} onChange={handleChange} />
            </div>
          </div>
        </section>

        {/* Address */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">{intl.formatMessage({ id: 'company-settings.address', defaultMessage: 'Address' })}</h2>
          </div>
          <div className="card-content form-grid three-col">
            <div className="form-field col-span-2">
              <label className="label" htmlFor="street">{intl.formatMessage({ id: 'company-settings.street', defaultMessage: 'Street' })}</label>
              <input id="street" name="street" className="input" type="text" value={form.street} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="number">{intl.formatMessage({ id: 'company-settings.number', defaultMessage: 'Number' })}</label>
              <input id="number" name="number" className="input" type="text" value={form.number} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="complement">{intl.formatMessage({ id: 'company-settings.complement', defaultMessage: 'Complement' })}</label>
              <input id="complement" name="complement" className="input" type="text" value={form.complement} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="neighborhood">{intl.formatMessage({ id: 'company-settings.neighborhood', defaultMessage: 'Neighborhood' })}</label>
              <input id="neighborhood" name="neighborhood" className="input" type="text" value={form.neighborhood} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="city">{intl.formatMessage({ id: 'company-settings.city', defaultMessage: 'City' })}</label>
              <input id="city" name="city" className="input" type="text" value={form.city} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="state">{intl.formatMessage({ id: 'company-settings.state', defaultMessage: 'State' })}</label>
              <input id="state" name="state" className="input" type="text" value={form.state} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="zip">{intl.formatMessage({ id: 'company-settings.zip', defaultMessage: 'ZIP' })}</label>
              <input id="zip" name="zip" className="input" type="text" value={form.zip} onChange={handleChange} />
            </div>
          </div>
        </section>

        {/* Contacts */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">{intl.formatMessage({ id: 'company-settings.contacts', defaultMessage: 'Contacts' })}</h2>
          </div>
          <div className="card-content form-grid three-col">
            <div className="form-field">
              <label className="label" htmlFor="companyEmail">{intl.formatMessage({ id: 'company-settings.company-email', defaultMessage: 'Company email' })}</label>
              <input id="companyEmail" name="companyEmail" className="input" type="email" value={form.companyEmail} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="companyPhone">{intl.formatMessage({ id: 'company-settings.company-phone', defaultMessage: 'Company phone' })}</label>
              <input id="companyPhone" name="companyPhone" className="input" type="tel" value={form.companyPhone} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label className="label" htmlFor="whatsapp">{intl.formatMessage({ id: 'company-settings.whatsapp', defaultMessage: 'WhatsApp' })}</label>
              <input id="whatsapp" name="whatsapp" className="input" type="tel" value={form.whatsapp} onChange={handleChange} />
            </div>
          </div>
        </section>

        {/* Preferences */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">{intl.formatMessage({ id: 'company-settings.preferences', defaultMessage: 'Preferences' })}</h2>
          </div>
          <div className="card-content form-grid two-col">
            <div className="form-field">
              <label className="label" htmlFor="language">{intl.formatMessage({ id: 'company-settings.language', defaultMessage: 'Language' })}</label>
              <select id="language" name="language" className="select" value={form.language} onChange={handleChange}>
                <option value="pt">Português (pt)</option>
                <option value="en">English (en)</option>
                <option value="es">Español (es)</option>
              </select>
            </div>
            <div className="form-field">
              <label className="label" htmlFor="timezone">{intl.formatMessage({ id: 'company-settings.timezone', defaultMessage: 'Timezone' })}</label>
              <select id="timezone" name="timezone" className="select" value={form.timezone} onChange={handleChange}>
                <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                <option value="America/Buenos_Aires">America/Buenos_Aires</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>
        </section>

        {/* Footer actions & overlays */}
        <div className="form-actions">
          <button className="btn primary" type="submit" disabled={saving || !isValid}>
            {saving ? intl.formatMessage({ id: 'common.saving' }) : intl.formatMessage({ id: 'common.save' })}
          </button>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="spinner" aria-label={intl.formatMessage({ id: 'common.loading' })} />
          </div>
        )}
      </form>
    </div>
  );
}
